import { Quaternion } from '@technology/web3d';
import {
  AlignmentNavigationCapability,
  PlanarCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import { SyncFn } from './sync-coordinator';
import {
  getActiveNavCap,
  navigateToClosestStation,
  updateFrustum,
} from './sync-source-helpers';
import { getAlignmentSyncPosition } from './source-positions';

/**
 * Syncs from a pane navigating along an alignment to a pane using station
 * navigation. Reads the alignment point from source, finds the closest
 * station in target, and navigates to it. Optionally syncs quaternion when
 * not in cross-section mode and target is not a `PlanarCapability`.
 *
 * NOTE: quaternion propagation is guarded by `crossSectionEnabled` — alignment
 * source orientation is otherwise always meaningful (the alignment tool
 * tracks the user's world-space look direction along the path).
 */
export const syncAlignmentToStation: SyncFn = async (source, target) => {
  const syncPosition = getAlignmentSyncPosition(source);
  if (!syncPosition) return;

  const sourceViewer = source.viewerWrapper()?.viewer;
  const targetViewer = target.viewerWrapper()?.viewer;
  if (!sourceViewer || !targetViewer) return;

  const alignmentCap = source.get(AlignmentNavigationCapability)();
  if (!alignmentCap) return;

  const targetCap = getActiveNavCap(target);
  if (!(targetCap instanceof StationsNavigationCapability)) return;

  const stationsProvider = target.get(StationsCapabilityProvider)();
  if (!stationsProvider) return;

  navigateToClosestStation(
    syncPosition,
    targetCap,
    stationsProvider,
    targetViewer
  );

  if (
    !alignmentCap.crossSectionEnabled() &&
    !(targetCap instanceof PlanarCapability)
  ) {
    targetCap.applyCameraOrientation(
      new Quaternion(...sourceViewer.camera.quaternion.toArray())
    );
  }

  updateFrustum(
    target,
    syncPosition,
    alignmentCap.crossSectionEnabled()
      ? null
      : sourceViewer.camera.quaternion.toArray(),
    null
  );
};
